/**
 * Dreamscape API Service - High-level wrapper around the SDK
 * Provides simplified functions for common operations
 */

import { createClient, DreamscapeClient } from './dreamscape-sdk';
import {
  CustomerCreate,
  DomainRegistrantCreate,
} from './dreamscape-sdk/types';
import {
  BadRequestException,
  NotFoundException,
  AuthenticationException,
  PaymentRequiredException,
} from './dreamscape-sdk/errors';

// Create SDK client instance
let sdkClient: DreamscapeClient | null = null;


function getClient(): DreamscapeClient {
  if (!sdkClient) {
    sdkClient = createClient();
  }
  return sdkClient;
}

// Note: shouldPreferSdk and getBackendApiUrl removed as per user request to remove all proxy requests.
// Direct SDK calls are now forced for all environments.

/**
 * Generate username from email
 */
function generateUsername(email: string): string {
  let base = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '_');
  if (base.length < 8) {
    base = base + '_' + Math.random().toString(36).slice(2, 8);
  }
  const suffix = '_' + Date.now().toString().slice(-6);
  const maxLen = 50;
  const allowedBaseLen = Math.max(3, maxLen - suffix.length);
  if (base.length > allowedBaseLen) {
    base = base.substring(0, allowedBaseLen);
  }
  return base + suffix;
}

/**
 * Generate secure password
 */
function generatePassword(): string {
  return Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-4).toUpperCase() + '!@#';
}

/**
 * Get country code
 */
import { findCountryDialCode } from "@/utils/countries";
function getCountryCode(country: string): string {
  return findCountryDialCode(country);
}

/**
 * Format phone number
 */
function formatPhone(phone: string, country: string): string {
  let cleaned = phone.replace(/[^\d+]/g, '');
  cleaned = cleaned.replace(/^\+/, '');
  const countryCode = getCountryCode(country);
  if (!cleaned.startsWith(countryCode)) {
    cleaned = countryCode + cleaned;
  }
  return cleaned;
}

/**
 * Domain search result (legacy interface for compatibility)
 */
export interface DomainSearchResult {
  domain: string;
  available: boolean;
  status?: string | null;
  registerPrice?: number | null;
  renewPrice?: number | null;
  checkingError?: string | null;
  currency?: string;
}

export interface DomainSearchResponse {
  success: boolean;
  results: DomainSearchResult[];
  message?: string;
}

/**
 * Validate domain name format
 */
function isValidDomainName(domain: string): boolean {
  if (!domain || typeof domain !== 'string') return false;

  // Remove trailing/leading dots and whitespace
  const cleanDomain = domain.trim().replace(/^\.+|\.+$/g, '');
  if (cleanDomain.length === 0) return false;

  // Check for empty parts (e.g., "domain..com" or ".domain.com")
  if (cleanDomain.includes('..') || cleanDomain.startsWith('.') || cleanDomain.endsWith('.')) {
    return false;
  }

  // Basic domain format validation
  const domainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;
  if (!domainRegex.test(cleanDomain)) {
    return false;
  }

  // Must have at least one dot (TLD)
  const parts = cleanDomain.split('.');
  if (parts.length < 2) return false;

  // All parts must be non-empty and not too long
  if (parts.some(part => part.length === 0 || part.length > 63)) return false;

  // Total length must not exceed 253 characters
  if (cleanDomain.length > 253) return false;

  return true;
}

/**
 * Clean and normalize domain name
 */
function cleanDomainName(domain: string): string | null {
  if (!domain || typeof domain !== 'string') return null;

  const cleaned = domain
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^\.+|\.+$/g, ''); // Remove leading/trailing dots

  if (!cleaned || cleaned.length === 0) return null;

  // Remove any double dots
  if (cleaned.includes('..')) return null;

  return cleaned;
}

/**
 * Search domains
 */
export async function searchDomains(
  domainName: string,
  tlds: string[] = ['com', 'net', 'org', 'io', 'co'],
  exactDomains?: string[],
  currency: string = 'USD'
): Promise<DomainSearchResponse> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests for all environments
  try {
    const client = getClient();
    let domainList: string[] = [];

    if (exactDomains && exactDomains.length > 0) {
      domainList = exactDomains.map(d => cleanDomainName(d)).filter((d): d is string => d !== null && isValidDomainName(d));
    } else {
      const cleanName = domainName.split('.')[0];
      domainList = tlds.map(tld => `${cleanName}.${tld}`);
    }

    if (domainList.length === 0) {
      return { success: true, results: [] };
    }

    console.log(`🔍 Searching ${domainList.length} domains via Direct Frontend SDK`);

    // Chunk domainList into batches of 5 to avoid API timeouts
    const CHUNK_SIZE = 5;
    const chunks: string[][] = [];
    for (let i = 0; i < domainList.length; i += CHUNK_SIZE) {
      chunks.push(domainList.slice(i, i + CHUNK_SIZE));
    }

    const chunkPromises = chunks.map(chunk =>
      client.domains.checkAvailability(chunk, { currency })
    );

    const chunkResults = await Promise.all(chunkPromises);

    // Combine all results
    let allItems: any[] = [];
    for (const res of chunkResults) {
      allItems = allItems.concat((res as any).items || (Array.isArray(res) ? res : []));
    }

    const results: DomainSearchResult[] = allItems.map((item: any) => {
      const getNum = (val: any): number | null => {
        if (typeof val === 'number') return val;
        if (!val) return null;
        const price = parseFloat(String(val).replace(/[^0-9.]/g, ''));
        return isNaN(price) ? null : price;
      };

      return {
        domain: item.domainName || item.domain_name || item.domain,
        available: item.isAvailable === true || item.is_available === true || item.available === true,
        registerPrice: getNum(item.registerPrice || item.display_price || item.register_price || item.price),
        renewPrice: getNum(item.renewPrice || item.renew_price),
        currency: item.currency || currency,
        status: item.status || item.status_text
      };
    });

    return { success: true, results };
  } catch (error) {
    console.error('Direct domain search error:', error);
    return {
      success: false,
      results: [],
      message: error instanceof Error ? error.message : 'Failed to search domains'
    };
  }
}

/**
 * Get available TLDs with prices
 */
export async function getAvailableTlds(filters?: {
  tlds?: string[];
  currency?: string;
}): Promise<{
  success: boolean;
  tlds?: any[];
  message?: string;
}> {
  try {
    const client = getClient();
    console.log(`📡 Fetching TLD prices via Direct SDK:`, filters);

    const result = await client.domains.getAvailableTlds({
      tlds: filters?.tlds,
      currency: filters?.currency || 'USD'
    });

    const items = result.items || (Array.isArray(result) ? result : []);

    return {
      success: true,
      tlds: items,
    };
  } catch (error) {
    console.error('getAvailableTlds SDK error:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Failed to get TLDs' };
  }
}

/**
 * Find customer by email (searches via Dreamscape SDK)
 */
export async function findCustomerByEmail(email: string): Promise<{
  success: boolean;
  customerId?: number;
  customer?: any;
  message?: string;
}> {
  try {
    const client = getClient();
    const targetEmail = email.trim().toLowerCase();

    console.log('[findCustomerByEmail] Searching for:', targetEmail);

    // Try multiple filter approaches
    const filterOptions = [
      { email: targetEmail },
      { username: targetEmail },
      { q: targetEmail } // Generic search if supported
    ];

    for (const filters of filterOptions) {
      console.log(`[findCustomerByEmail] Trying filters:`, filters);
      const result = await client.customers.getAll(filters);

      // Robustly extract items from various possible response formats
      let items: any[] = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result && typeof result === 'object') {
        if (Array.isArray(result.items)) {
          items = result.items;
        } else if (Array.isArray((result as any).data)) {
          items = (result as any).data;
        } else if ((result as any).id) {
          // Result might be a single customer object
          items = [result];
        }
      }

      console.log(`[findCustomerByEmail] Found ${items.length} potential matches with filters:`, filters);

      if (items.length > 0) {
        // Find exact match (email or username might be in snake_case OR camelCase from API)
        const match = items.find((c: any) => {
          const cEmail = (c.email || c.email_address || c.emailAddress || '').toLowerCase();
          const cUsername = (c.username || c.user_name || '').toLowerCase();
          return cEmail === targetEmail || cUsername === targetEmail;
        });

        if (match) {
          console.log('[findCustomerByEmail] Found exact match:', match.id);
          return {
            success: true,
            customerId: match.id,
            customer: match
          };
        }
      }
    }

    // Fallback: If still not found, try getting page 1 and searching manual
    // This handles the case where filters are completely ignored
    console.log('[findCustomerByEmail] No match with filters, trying manual search on first page');
    const firstPage = await client.customers.getAll({ limit: 100 });
    let manualItems: any[] = [];
    if (Array.isArray(firstPage)) manualItems = firstPage;
    else if (firstPage && Array.isArray(firstPage.items)) manualItems = firstPage.items;

    const manualMatch = manualItems.find((c: any) => {
      const cEmail = (c.email || c.email_address || c.emailAddress || '').toLowerCase();
      const cUsername = (c.username || c.user_name || '').toLowerCase();
      return cEmail === targetEmail || cUsername === targetEmail;
    });

    if (manualMatch) {
      return {
        success: true,
        customerId: manualMatch.id,
        customer: manualMatch
      };
    }

    return {
      success: false,
      message: `Customer persistent record for ${targetEmail} not found in the API. If you just registered, please wait a moment or try logging in with your generated username if you have it.`
    };
  } catch (error) {
    console.error('[findCustomerByEmail] Error:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return {
        success: false,
        message: 'Could not connect to Dreamscape API. This may be a network or CORS issue.'
      };
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to search for customer'
    };
  }
}


/**
 * Create customer (enhanced with automatic username/password generation)
 */
export async function createCustomer(contactInfo: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  organization?: string;
  accountType?: 'personal' | 'business';
  businessName?: string;
  businessNumberType?: string;
  businessNumber?: string;
}, options?: {
  password?: string;
}): Promise<{
  success: boolean;
  customerId?: number;
  message?: string;
  username?: string;
  password?: string;
}> {
  try {
    const client = getClient();

    const email = contactInfo.email.trim().toLowerCase();
    const username = generateUsername(email);
    const password = options?.password && options.password.trim().length >= 8
      ? options.password.trim()
      : generatePassword();

    // Validate password length again just in case
    if (password.length < 8) {
      return {
        success: false,
        message: 'Password must be at least 8 characters long as per API requirements.',
      };
    }

    const countryCode = getCountryCode(contactInfo.country);
    const phone = formatPhone(contactInfo.phone, contactInfo.country);

    const customerData: CustomerCreate = {
      username,
      password,
      firstName: contactInfo.firstName.trim(),
      lastName: contactInfo.lastName.trim(),
      email,
      phone,
      mobile: phone,
      address: contactInfo.address.trim(),
      city: contactInfo.city.trim(),
      state: contactInfo.state.trim(),
      postCode: contactInfo.zip.trim(),
      country: contactInfo.country.trim().toUpperCase(),
      countryCode,
      accountType: contactInfo.accountType || (contactInfo.organization ? 'business' : 'personal'),
      businessName: contactInfo.businessName?.trim() || contactInfo.organization?.trim(),
      businessNumber: contactInfo.businessNumber?.trim(),
      businessNumberType: contactInfo.businessNumberType?.trim(),
    };

    console.log('Creating customer with direct API call:', { ...customerData, password: '***' });
    const customer = await client.customers.create(customerData);

    if (!customer || !customer.id) {
      throw new Error('API returned successfully but customer ID is missing.');
    }

    return {
      success: true,
      customerId: customer.id,
      username,
      password,
    };
  } catch (error) {
    console.error('Customer registration API error:', error);

    let errorMessage = 'Failed to create customer';
    if (error instanceof AuthenticationException) {
      // Pass through authentication errors (like IP whitelist issues)
      errorMessage = error.message;
    } else if (error instanceof BadRequestException) {
      const details = Array.isArray(error.validationErrors)
        ? error.validationErrors.join(', ')
        : (typeof error.validationErrors === 'object' ? JSON.stringify(error.validationErrors) : '');
      errorMessage = details ? `Validation failed: ${details}` : (error.message || 'Invalid customer data');
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
}

/**
 * Create domain registrant contact
 */
export async function createRegistrant(contactInfo: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  organization?: string;
}): Promise<{
  success: boolean;
  registrantId?: number;
  message?: string;
}> {
  try {
    const client = getClient();

    const countryCode = getCountryCode(contactInfo.country);
    const phone = formatPhone(contactInfo.phone, contactInfo.country);

    const registrantData: DomainRegistrantCreate = {
      firstName: contactInfo.firstName.trim(),
      lastName: contactInfo.lastName.trim(),
      email: contactInfo.email.trim().toLowerCase(),
      phone,
      address: contactInfo.address.trim(),
      city: contactInfo.city.trim(),
      state: contactInfo.state.trim(),
      postCode: contactInfo.zip.trim(),
      country: contactInfo.country.trim().toUpperCase(),
      countryCode,
      accountType: contactInfo.organization ? 'business' : 'personal',
      businessName: contactInfo.organization?.trim(),
    };

    const registrant = await client.domains.registrants.create(registrantData);

    return {
      success: true,
      registrantId: registrant.id,
    };
  } catch (error) {
    if (error instanceof BadRequestException) {
      const details = Array.isArray(error.validationErrors)
        ? error.validationErrors.join(', ')
        : (typeof error.validationErrors === 'object' ? JSON.stringify(error.validationErrors) : '');
      return {
        success: false,
        message: details ? `Invalid registrant data: ${details}` : (error.message || 'Invalid registrant data'),
      };
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to create registrant',
    };
  }
}

/**
 * Register domain (Using our backend automated checkout flow)
 */
export async function registerDomain(
  domain: string,
  contactInfo: any,
  options?: any
): Promise<any> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests for all environments
  try {
    const client = getClient();
    let customerId = options?.customerId;

    // Get or create customer if not provided
    if (!customerId || customerId <= 0) {
      const customerResult = await createCustomer(contactInfo);
      if (!customerResult.success || !customerResult.customerId) {
        return { success: false, message: customerResult.message || 'Failed to create customer' };
      }
      customerId = customerResult.customerId;
      // Small delay to ensure customer is fully created
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Create registrant
    const registrantResult = await createRegistrant(contactInfo);
    if (!registrantResult.success || !registrantResult.registrantId) {
      return {
        success: false,
        message: registrantResult.message || 'Failed to create registrant contact',
        customerId,
        showContactForm: true
      };
    }
    const registrantId = registrantResult.registrantId;

    // Register domain
    const periodMonths = (options?.period || 1) * 12;
    const domainData = {
      domainName: domain,
      customerId: customerId,
      period: periodMonths,
      registrantId: registrantId,
      privacy: options?.privacy
    };

    console.log(`📡 Registering ${domain} via Direct SDK`);
    const registered = await client.domains.register(domainData);

    return {
      success: true,
      orderId: registered.id?.toString(),
      domainId: registered.id,
      customerId,
      registrantId,
      message: 'Domain registered successfully via SDK'
    };
  } catch (error: any) {
    console.error('Registration error (SDK):', error);

    // Handle payment required error
    if (error instanceof PaymentRequiredException || error.status === 402) {
      return {
        success: false,
        isPaymentError: true,
        message: error.message || 'Payment is required to complete registration',
        orderId: domain, // Use domain as temporary identifier
        customerId: options?.customerId
      };
    }

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to register domain'
    };
  }
}

/**
 * Get all product types (for debugging)
 */
export async function getAllProductTypes(): Promise<{
  success: boolean;
  types?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    // Robust fetch for product types
    let response;
    try {
      response = await client.request<any>('GET', 'product/types');
    } catch (e1) {
      try {
        response = await client.request<any>('GET', 'product-types');
      } catch (e2) {
        response = await client.request<any>('GET', 'products/types');
      }
    }
    const types = response?.items || response?.data || response || [];

    return {
      success: true,
      types: Array.isArray(types) ? types : [types],
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get product types',
    };
  }
}

/**
 * Get product plans by type
 */
export async function getProductPlans(typeId?: number, filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    // typeId is required for filtering - if not provided, return error
    if (!typeId) {
      return {
        success: false,
        message: 'Product type ID is required to fetch plans',
      };
    }
    params.typeId = typeId;
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Use the products endpoint to get plans filtered by typeId
    const response = await client.request<any>('GET', 'products/plans', undefined, params);

    // Handle different response formats
    let plans: any[] = [];
    if (Array.isArray(response)) {
      plans = response;
    } else if (response?.items && Array.isArray(response.items)) {
      plans = response.items;
    } else if (response && typeof response === 'object') {
      // Single plan object
      plans = [response];
    }

    // Helper to extract numeric value from various price structures
    const extractPrice = (period: any): number | null => {
      if (!period) return null;

      const getNum = (val: any): number | null => {
        if (val === null || val === undefined) return null;
        const price = typeof val === 'number' ? val : parseFloat(String(val));
        return !isNaN(price) && price > 0 ? price : null;
      };

      // Try various field names for register price
      let p = getNum(period.registerPrice)
        || getNum(period.register_price)
        || getNum(period.price_register)
        || getNum(period.registrationPrice)
        || getNum(period.registration_price);

      // Handle price objects
      const priceObjects = [period.price, period.prices, period.pricing];
      for (const obj of priceObjects) {
        if (obj && typeof obj === 'object') {
          if (p === null) {
            p = getNum(obj.register) || getNum(obj.registration) || getNum(obj.price) || getNum(obj.amount);
          }
        }
      }

      // Final fallback to numeric period object if it's just a number
      if (p === null && typeof period.price === 'number') p = period.price;

      return p;
    };

    // Additional filter: ensure all returned plans match the requested typeId
    const filteredPlans = plans.filter((plan: any) => {
      // Check if plan has typeId property and it matches
      if (plan.typeId && plan.typeId !== typeId) {
        return false;
      }
      // Ensure plan has required structure
      if (!plan || !plan.id || !plan.name) {
        return false;
      }
      // Filter out unwanted product types by name
      const planName = (plan.name || '').toLowerCase();
      const excludedKeywords = ['fax', 'private registration', 'dns', 'domain privacy', 'site protection'];
      if (excludedKeywords.some(keyword => planName.includes(keyword))) {
        return false;
      }
      // Ensure plan has valid periods with prices
      if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) {
        return false;
      }
      // Ensure at least one period has a valid price
      const hasValidPrice = plan.periods.some((p: any) => {
        const price = extractPrice(p);
        return price !== null && price > 0;
      });
      if (!hasValidPrice) {
        if (import.meta.env.DEV) {
          console.warn(`Plan ${plan.id} (${plan.name}) filtered out - no valid price. Periods:`, plan.periods);
        }
        return false;
      }
      return true;
    });

    if (import.meta.env.DEV && filteredPlans.length !== plans.length) {
      console.log(`Filtered ${plans.length - filteredPlans.length} invalid plans. Remaining: ${filteredPlans.length}`);
    }

    // Fetch features for each plan
    const plansWithFeatures = await Promise.all(
      filteredPlans.map(async (plan: any) => {
        try {
          // Fetch features for this plan
          // Request common hosting feature types
          const featuresResult = await getProductPlanFeatures(plan.id, {
            types: ['core', 'hosting_manager', 'disk_space', 'webspace', 'storage', 'bandwidth',
              'email', 'email_accounts', 'database', 'databases', 'mysql',
              'websites', 'website', 'domains', 'spam_protection', 'virus_protection',
              'spam_virus', 'security', 'ssl', 'ssl_certificate', 'backup', 'backups']
          });

          // Map features to component format
          if (featuresResult.success && featuresResult.features) {
            plan.features = mapFeaturesToComponentFormat(featuresResult.features);
          } else {
            // If features fetch fails, set empty array
            plan.features = [];
          }
        } catch (error) {
          // If feature fetch fails, continue without features
          if (import.meta.env.DEV) {
            console.warn(`Failed to fetch features for plan ${plan.id}:`, error);
          }
          plan.features = [];
        }

        return plan;
      })
    );

    return {
      success: true,
      plans: plansWithFeatures,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get product plans',
    };
  }
}

/**
 * Get product plan features/services
 */
export async function getProductPlanFeatures(planId: number, filters?: {
  types?: string[];
}): Promise<{
  success: boolean;
  features?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.types && filters.types.length > 0) {
      params.types = filters.types;
    }

    const response = await client.request<any>('GET', `products/plans/${planId}/features`, undefined, params);

    // Handle different response formats
    let features: any[] = [];
    if (Array.isArray(response)) {
      features = response;
    } else if (response?.items && Array.isArray(response.items)) {
      features = response.items;
    } else if (response && typeof response === 'object') {
      features = [response];
    }

    return {
      success: true,
      features,
    };
  } catch (error) {
    // Don't fail the whole request if features can't be fetched
    if (import.meta.env.DEV) {
      console.warn(`Failed to fetch features for plan ${planId}:`, error);
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get product plan features',
    };
  }
}

/**
 * Map API features to component format
 */
function mapFeaturesToComponentFormat(apiFeatures: any[]): Array<{ name: string; value?: string }> {
  const mappedFeatures: Array<{ name: string; value?: string }> = [];

  if (!apiFeatures || !Array.isArray(apiFeatures)) {
    return mappedFeatures;
  }

  // Common feature type mappings for better display names
  const featureTypeMap: Record<string, string> = {
    'disk_space': 'Webspace',
    'webspace': 'Webspace',
    'storage': 'Webspace',
    'disk': 'Webspace',
    'bandwidth': 'Bandwidth',
    'email': 'Email Addresses',
    'email_accounts': 'Email Addresses',
    'emails': 'Email Addresses',
    'database': 'Databases',
    'databases': 'Databases',
    'mysql': 'Databases',
    'mssql': 'MSSQL Databases',
    'websites': 'Websites',
    'website': 'Websites',
    'domains': 'Websites',
    'domain': 'Websites',
    'spam_protection': 'Spam & Virus Protection',
    'virus_protection': 'Spam & Virus Protection',
    'spam_virus': 'Spam & Virus Protection',
    'security': 'Spam & Virus Protection',
    'ssl': 'SSL Certificate',
    'ssl_certificate': 'SSL Certificate',
    'backup': 'Backups',
    'backups': 'Backups',
    'hosting_manager': 'Control Panel',
    'control_panel': 'Control Panel',
    'cpanel': 'cPanel',
    'php': 'PHP Versions',
    'php_versions': 'PHP Versions',
    'subdomains': 'Subdomains',
    'ftp': 'FTP Accounts',
    'ftp_accounts': 'FTP Accounts',
    'dedicated_ip': 'Dedicated IP',
    'ip_address': 'IP Address',
    'memory': 'Memory',
    'ram': 'RAM',
    'cpu': 'CPU',
    'core': 'Core Features',
  };

  for (const feature of apiFeatures) {
    // Feature has structure: { type: string, values: [{ value: string, periods: [...] }] }
    const featureType = feature.type || '';
    const values = feature.values || [];

    // Skip if no type
    if (!featureType) {
      continue;
    }

    // Get display name from map or format it
    const displayName = featureTypeMap[featureType.toLowerCase()] ||
      featureType
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    // Get the first value (or combine all values)
    if (values.length === 1) {
      const value = values[0].value || '';
      if (value) {
        mappedFeatures.push({
          name: displayName,
          value: value,
        });
      } else {
        // If no value but feature exists, just show the name
        mappedFeatures.push({
          name: displayName,
        });
      }
    } else if (values.length > 1) {
      // If multiple values, show them as a comma-separated list
      const valueList = values.map((v: any) => v.value).filter(Boolean).join(', ');
      if (valueList) {
        mappedFeatures.push({
          name: displayName,
          value: valueList,
        });
      } else {
        mappedFeatures.push({
          name: displayName,
        });
      }
    } else {
      // Just the type name if no values (feature is included but no specific value)
      mappedFeatures.push({
        name: displayName,
      });
    }
  }

  return mappedFeatures;
}

/**
 * Get product plan details
 */
export async function getProductPlanDetails(planId: number): Promise<{
  success: boolean;
  plan?: any;
  message?: string;
}> {
  try {
    const client = getClient();

    const plan = await client.request<any>('GET', `products/plans/${planId}`);

    return {
      success: true,
      plan,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get product plan details',
    };
  }
}

/**
 * Get WordPress Hosting product plans
 */
export async function getWordPressHostingPlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Get all product types first to find WordPress Hosting type ID
    const typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
    const types = typesResponse.items || typesResponse || [];

    // Find WordPress Hosting type - be more specific
    const wordPressHostingType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        return name.includes('wordpress') && name.includes('hosting');
      })
      : null;

    const typeId = wordPressHostingType?.id;

    if (!typeId) {
      return {
        success: false,
        message: 'WordPress Hosting product type not found. Please contact support.',
      };
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns', 'ssl', 'domain privacy'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) return false;

        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });
        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        return {
          success: false,
          message: 'No valid WordPress hosting plans found.',
        };
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get WordPress hosting plans',
    };
  }
}

/**
 * Get Windows Hosting product plans
 */
export async function getWindowsHostingPlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Robust fetch for product types
    let typesResponse;
    try {
      typesResponse = await client.request<any>('GET', 'product/types', undefined, params);
    } catch (e1) {
      try {
        typesResponse = await client.request<any>('GET', 'product-types', undefined, params);
      } catch (e2) {
        typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
      }
    }
    const types = typesResponse?.items || typesResponse?.data || typesResponse || [];

    // Find Windows Hosting type - be more specific to avoid matching Windows Server
    const windowsHostingType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        return name.includes('windows') && name.includes('hosting') && !name.includes('server');
      })
      : null;

    const typeId = windowsHostingType?.id;

    if (!typeId) {
      return {
        success: false,
        message: 'Windows Hosting product type not found. Please contact support.',
      };
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns', 'ssl', 'domain privacy'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) return false;

        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });
        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        return {
          success: false,
          message: 'No valid Windows hosting plans found.',
        };
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get Windows hosting plans',
    };
  }
}

/**
 * Get Linux/Web Hosting product plans
 */
export async function getLinuxHostingPlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Get all product types first to find Linux Hosting type ID
    // Try singular 'product/types' which is standard for Dreamscape/NetEarthOne
    let typesResponse;
    try {
      typesResponse = await client.request<any>('GET', 'product/types', undefined, params);
    } catch (e) {
      console.warn('products/types failed, trying product-types...', e);
      try {
        typesResponse = await client.request<any>('GET', 'product-types', undefined, params);
      } catch (e2) {
        console.warn('product-types failed, trying products/types...', e2);
        typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
      }
    }
    const types = typesResponse?.items || typesResponse?.data || typesResponse || [];

    // Log available types for debugging (only in dev mode)
    if (import.meta.env.DEV) {
      console.log('Available product types:', types.map((t: any) => ({ id: t.id, name: t.name })));
    }

    // Find Linux Hosting type - be more specific to avoid matching wrong types
    const linuxHostingType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        // Match "Linux Hosting" or "Web Hosting" but exclude "Windows", "WordPress", "Email", "Fax", etc.
        return name.includes('hosting') &&
          (name.includes('linux') || (name.includes('web') && !name.includes('windows') && !name.includes('wordpress'))) &&
          !name.includes('email') &&
          !name.includes('fax') &&
          !name.includes('dns') &&
          !name.includes('ssl') &&
          !name.includes('private');
      })
      : null;

    const typeId = linuxHostingType?.id;

    if (!typeId) {
      // Log available types for debugging
      console.warn('Linux Hosting type not found. Available types:', types.map((t: any) => t.name));
      return {
        success: false,
        message: 'Linux Hosting product type not found. Please contact support.',
      };
    }

    if (import.meta.env.DEV) {
      console.log('Found Linux Hosting type:', { id: typeId, name: linuxHostingType.name });
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        // Basic structure validation
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        // Filter out unwanted product types by name
        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns hosting', 'ssl', 'domain privacy', 'site protection', 'site builder'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) {
          return false;
        }

        // Ensure it's actually a hosting plan
        if (!planName.includes('hosting') && !planName.includes('basic') && !planName.includes('standard') && !planName.includes('business')) {
          // Allow if it's a valid hosting plan name pattern
          const hostingPatterns = ['starter', 'professional', 'enterprise', 'premium'];
          if (!hostingPatterns.some(pattern => planName.includes(pattern))) {
            return false;
          }
        }

        // Ensure at least one period has a valid price
        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });

        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        console.warn('No valid Linux hosting plans found after filtering. Total plans received:', result.plans.length);
        return {
          success: false,
          message: 'No valid Linux hosting plans found.',
        };
      }

      if (import.meta.env.DEV) {
        console.log(`Linux Hosting: Filtered ${result.plans.length} plans to ${validPlans.length} valid plans`);
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get Linux hosting plans',
    };
  }
}

/**
 * Get Windows Server product plans
 */
export async function getWindowsServerPlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Get all product types first to find Windows Server type ID
    const typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
    const types = typesResponse.items || typesResponse || [];

    // Find Windows Server type - be more specific
    const windowsServerType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        return name.includes('windows') &&
          name.includes('server') &&
          !name.includes('hosting');
      })
      : null;

    const typeId = windowsServerType?.id;

    if (!typeId) {
      return {
        success: false,
        message: 'Windows Server product type not found. Please contact support.',
      };
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns', 'ssl', 'domain privacy', 'hosting'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) return false;

        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });
        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        return {
          success: false,
          message: 'No valid Windows Server plans found.',
        };
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get Windows Server plans',
    };
  }
}

/**
 * Get Email Exchange product plans
 */
export async function getEmailExchangePlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Get all product types first to find Email Exchange type ID
    const typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
    const types = typesResponse.items || typesResponse || [];

    // Find Email Exchange type - be more specific
    const emailExchangeType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        return name.includes('email') &&
          name.includes('exchange') &&
          !name.includes('hosting') &&
          !name.includes('marketing');
      })
      : null;

    const typeId = emailExchangeType?.id;

    if (!typeId) {
      return {
        success: false,
        message: 'Email Exchange product type not found. Please contact support.',
      };
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns', 'ssl', 'domain privacy'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) return false;

        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });
        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        return {
          success: false,
          message: 'No valid Email Exchange plans found.',
        };
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get Email Exchange plans',
    };
  }
}

/**
 * Get Simple SEO product plans
 */
export async function getSimpleSeoPlans(filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  plans?: any[];
  message?: string;
}> {
  try {
    const client = getClient();

    const params: Record<string, any> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;

    // Get all product types first to find Simple SEO type ID
    const typesResponse = await client.request<any>('GET', 'products/types', undefined, params);
    const types = typesResponse.items || typesResponse || [];

    // Find Simple SEO type - be more specific
    const simpleSeoType = Array.isArray(types)
      ? types.find((t: any) => {
        const name = (t.name || '').toLowerCase();
        return (name.includes('simple') && name.includes('seo')) ||
          (name.includes('seo') && !name.includes('marketing'));
      })
      : null;

    const typeId = simpleSeoType?.id;

    if (!typeId) {
      return {
        success: false,
        message: 'Simple SEO product type not found. Please contact support.',
      };
    }

    const result = await getProductPlans(typeId, filters);

    // Validate that plans have valid data and filter out unwanted products
    if (result.success && result.plans) {
      const validPlans = result.plans.filter((plan: any) => {
        if (!plan || !plan.id || !plan.name) return false;
        if (!plan.periods || !Array.isArray(plan.periods) || plan.periods.length === 0) return false;

        const planName = (plan.name || '').toLowerCase();
        const excludedKeywords = ['fax', 'private registration', 'dns', 'ssl', 'domain privacy', 'hosting'];
        if (excludedKeywords.some(keyword => planName.includes(keyword))) return false;

        const hasValidPrice = plan.periods.some((p: any) => {
          // Try multiple price fields
          const priceObj = p.price || {};
          const price = priceObj.register ?? priceObj.renew ?? priceObj.wholesale ?? (typeof priceObj === 'number' ? priceObj : null);
          return price !== null && price !== undefined && !isNaN(price) && price > 0;
        });
        return hasValidPrice;
      });

      if (validPlans.length === 0) {
        return {
          success: false,
          message: 'No valid Simple SEO plans found.',
        };
      }

      return {
        success: true,
        plans: validPlans,
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get Simple SEO plans',
    };
  }
}

/**
 * Get customer details by ID
 */
export async function getCustomerDetails(customerId: number): Promise<{
  success: boolean;
  customer?: any;
  message?: string;
}> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests
  try {
    const client = getClient();
    const raw = await client.customers.getDetails(customerId);

    // Robust mapping for SDK response
    const customer: any = { ...raw };
    customer.firstName = raw.firstName || (raw as any).first_name || (raw as any).given_name || '';
    customer.lastName = raw.lastName || (raw as any).last_name || (raw as any).family_name || '';
    customer.email = raw.email || (raw as any).email_address || '';
    customer.phone = raw.phone || (raw as any).phone_number || (raw as any).mobile || '';
    customer.address = raw.address || (raw as any).address_1 || '';
    customer.city = raw.city || '';
    customer.state = raw.state || (raw as any).province || '';
    customer.postCode = raw.postCode || (raw as any).post_code || '';
    customer.country = raw.country || '';

    return { success: true, customer };
  } catch (error) {
    console.error('getCustomerDetails error (SDK):', error);
    return { success: false, message: error instanceof Error ? error.message : 'Failed to get customer details' };
  }
}

/**
 * Create customer login link for admin panel access
 */
export async function createCustomerLoginLink(customerId: number): Promise<{
  success: boolean;
  loginLink?: string;
  message?: string;
}> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests
  try {
    const client = getClient();
    const result = await client.customers.createLoginLink(customerId);
    return { success: true, loginLink: result.link || (result as any).url };
  } catch (error) {
    console.error('createCustomerLoginLink error (SDK):', error);
    return { success: false, message: error instanceof Error ? error.message : 'Failed to create login link' };
  }
}

/**
 * Update customer details
 */
export async function updateCustomerDetails(customerId: number, data: any): Promise<{
  success: boolean;
  message?: string;
  customer?: any;
}> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests
  try {
    const client = getClient();
    const result = await client.customers.update(customerId, data);
    return { success: true, customer: result };
  } catch (error) {
    console.error('updateCustomerDetails error (SDK):', error);
    return { success: false, message: error instanceof Error ? error.message : 'Failed to update profile' };
  }
}

/**
 * Update customer password
 */
export async function updateCustomerPassword(customerId: number, newPassword: string): Promise<{
  success: boolean;
  message?: string;
}> {
  try {
    const client = getClient();
    await client.customers.update(customerId, { password: newPassword });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update customer password',
    };
  }
}

/**
 * Get customer domains
 */
export async function getCustomerDomains(customerId: number, filters?: {
  statusId?: number;
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  domains?: any[];
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.domains.getAll({
      customerId,
      ...filters
    });

    let items: any[] = [];
    if (Array.isArray(result)) {
      items = result;
    } else if (result && typeof result === 'object') {
      if (Array.isArray(result.items)) {
        items = result.items;
      } else if (Array.isArray((result as any).data)) {
        items = (result as any).data;
      }
    }

    return {
      success: true,
      domains: items,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get customer domains',
    };
  }
}

/**
 * Get customer invoices/finances
 */
export async function getCustomerInvoices(customerId: number, filters?: {
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  invoices?: any[];
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.finances.getInvoices({
      customerId,
      ...filters
    });

    let items: any[] = [];
    if (Array.isArray(result)) {
      items = result;
    } else if (result && typeof result === 'object') {
      if (Array.isArray(result.items)) {
        items = result.items;
      } else if (Array.isArray((result as any).data)) {
        items = (result as any).data;
      }
    }

    return {
      success: true,
      invoices: items,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get customer invoices',
    };
  }
}

/**
 * Get customer orders from local database tracking
 */
export async function getCustomerOrders(email: string): Promise<{
  success: boolean;
  orders?: any[];
  message?: string;
}> {
  // Direct Dreamscape SDK calls (no proxy) - Force direct requests
  try {
    const client = getClient();
    // First find customer to get ID
    const customerResult = await findCustomerByEmail(email);
    if (!customerResult.success || !customerResult.customerId) {
      return { success: true, orders: [], message: 'Standalone mode: Customer record doesn\'t exist in API yet' };
    }

    const result = await client.finances.getInvoices({ customerId: customerResult.customerId });
    const items = (result && (result as any).items) || (Array.isArray(result) ? result : []);
    return { success: true, orders: items };
  } catch (error) {
    console.error('getCustomerOrders error (SDK):', error);
    return { success: false, orders: [], message: 'Standalone mode: Failed to fetch order history from SDK' };
  }
}

/**
 * Update Domain Name Servers via SDK
 */
export async function updateDomainNameServers(domainId: number, nameServers: Array<{ host: string }>): Promise<{
  success: boolean;
  domain?: any;
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.domains.update(domainId, { nameServers });
    return {
      success: true,
      domain: result,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update name servers',
    };
  }
}

/**
 * Toggle Domain Lock via SDK
 */
export async function toggleDomainLock(domainId: number, isLocked: boolean): Promise<{
  success: boolean;
  domain?: any;
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.domains.update(domainId, { isLocked });
    return { success: true, domain: result };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update domain lock',
    };
  }
}

/**
 * Renew domain via SDK
 */
export async function renewDomain(domainId: number, period: number = 12): Promise<{
  success: boolean;
  domain?: any;
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.domains.renew(domainId, { period });
    return { success: true, domain: result };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to renew domain',
    };
  }
}

/**
 * Get Domain Details via SDK
 */
export async function getDomainDetails(domainId: number): Promise<{
  success: boolean;
  domain?: any;
  message?: string;
}> {
  try {
    const client = getClient();
    const result = await client.domains.getDetails(domainId);
    return { success: true, domain: result };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to get domain details',
    };
  }
}

/**
 * Helper function to format error messages, especially for "not found" errors
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof NotFoundException) {
    // Use the getUserMessage method for better error messages
    return error.getUserMessage();
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Helper function to check if an error is a "not found" error
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof NotFoundException;
}

/**
 * Helper function to get user-friendly message for "Message not found" errors
 */
export function getMessageNotFoundError(messageId?: string | number): string {
  if (messageId) {
    return `Message with ID "${messageId}" not found. It may have been deleted or you may not have permission to view it.`;
  }
  return 'Message not found. It may have been deleted or you may not have permission to view it.';
}
